import React from "react";
import {
  Page,
  Layout,
  Text,
  Card,
  Box,
  Button,
  Modal,
  TextField,
  FormLayout,
  Banner,
  InlineStack,
  Badge,
  BlockStack,
} from "@shopify/polaris";
import {
} from "@shopify/polaris-icons";

import { DemoModeProvider, TooltipDemoOnly as Tooltip } from "./TooltipDemoOnly";

import { AnalyticsOverview } from "./AnalyticsOverview";
import { SupportCard } from "./SupportCard";
import { QueueItem } from "./QueueItem";
import { TruncatedNote } from "./TruncatedNote";
import { UsageBalanceCard } from "./UsageBalanceCard";
import { AgentHealthCard } from "./AgentHealthCard";
import { IntegrationSettingsCard } from "./IntegrationSettingsCard";
import { DangerZoneCard } from "./DangerZoneCard";
import {
  getIntentTone,
  formatIntentText,
  formatActionText,
} from "../utils/formatters";

export interface PublicDashboardViewProps {
  // Data
  returns: any[];
  automatedCount: number;
  settings: any;
  revenueData: any[];
  intentData: any[];
  totalSavedRevenue: number;
  automationRate: number;
  annualizedSavings: number;
  retentionLift: number;
  monthlyAutomatedCount: number;
  monthlyRevenueSaved: number;
  monthlyRetentionLift: number;
  monthlyTotalInferences: number;
  monthlyFeesCharged: number;
  avgSavedOrder: number;
  roiMultiple: number;
  planName: string;
  isAdmin: boolean;
  usageLimitReached: boolean;
  usageWarning: boolean;
  monthlyProcessed: number;
  appEnv: string;
  isDemoMode: boolean;
  isReadOnly?: boolean;
  isReviewerStore?: boolean;
  justSubscribed: boolean;
  rechargeConnected: boolean;
  loopConnected: boolean;
  supportTickets: any[];
  recordCount: number;
  sessionShop: string;
  shopHandle: string;
  shopLocale: string;
  shopCurrency: string;
  shopTimezone: string;
  activeJobs: any[];
  noSubscriptionReturns: any[];
  thresholdImpact: any;

  // State from Route
  mounted: boolean;
  lastRefreshed: Date | null;
  revalidator: any;
  fetcher: any;
  downloadFetcher: any;
  downloadAllFetcher: any;

  // Handlers
  onOpenSupport: () => void;
  onNavigate: (path: string) => void;
  onThresholdChange: (val: number) => void;
  onDownloadCSV: () => void;
  onDownloadAllCSV: () => void;
  onDeleteHistory: () => void;
  onSubmitFeedback: (text: string) => void;
  onDeleteTicket: (id: string) => void;

  // Demo Specific
  simulationTools?: React.ReactNode;

  isNavigatingPricing?: boolean;
}

export const PublicDashboardView: React.FC<PublicDashboardViewProps> = (props) => {
  const {
    returns,
    settings,
    revenueData,
    intentData,
    totalSavedRevenue,
    monthlyRevenueSaved,
    monthlyFeesCharged,
    monthlyTotalInferences,
    automationRate,
    annualizedSavings,
    retentionLift,
    roiMultiple,
    avgSavedOrder,
    shopLocale,
    shopCurrency,
    planName,
    appEnv,
    isDemoMode,
    isReadOnly = false,
    monthlyProcessed,
    supportTickets,
    recordCount,
    sessionShop,
    shopHandle,
    thresholdImpact,
    activeJobs,
    fetcher,
    onOpenSupport,
    onThresholdChange,
    onDownloadCSV,
    onDownloadAllCSV,
    onDeleteHistory,
    onSubmitFeedback,
    onDeleteTicket,
  } = props;

  const [feedbackModalOpen, setFeedbackModalOpen] = React.useState(false);
  const [feedbackText, setFeedbackText] = React.useState("");
  const [showStatusBanner, setShowStatusBanner] = React.useState(false);
  const [bannerTicketId, setBannerTicketId] = React.useState<string | null>(null);

  React.useEffect(() => {
    // Ensure we are in browser
    if (typeof window === "undefined") return;

    let foundUpdate = false;
    for (const ticket of (supportTickets || [])) {
      if (ticket.status !== "Open" && ticket.status !== "Sent") {
        const storedStatus = localStorage.getItem(`lastSeenStatus_${ticket.id}`);
        // If the current status is different from what we last acknowledged, show banner
        if (storedStatus !== ticket.status) {
          setShowStatusBanner(true);
          setBannerTicketId(ticket.id);
          foundUpdate = true;
          break;
        }
      }
    }

    // If no updates found, ensure banner is hidden
    if (!foundUpdate) {
      setShowStatusBanner(false);
    }
  }, [supportTickets]);

  const handleDismissBanner = () => {
    if (bannerTicketId) {
      const ticket = (supportTickets || []).find((t: any) => t.id === bannerTicketId);
      if (ticket) {
        localStorage.setItem(`lastSeenStatus_${ticket.id}`, ticket.status);
      }
    }
    setShowStatusBanner(false);
  };

  const [activeThreshold, setActiveThreshold] = React.useState(settings?.confidenceThreshold ?? 0.85);

  React.useEffect(() => {
    if (settings?.confidenceThreshold !== undefined) {
      setActiveThreshold(settings.confidenceThreshold);
    }
  }, [settings?.confidenceThreshold]);

  const noSubscriptionReturns = React.useMemo(() =>
    (returns || []).filter((r: any) => r.status === "NO_SUBSCRIPTION"),
    [returns]
  );

  const pendingReview = React.useMemo(() =>
    (returns || []).filter((r: any) => {
      const isPendingStatus = r.status === "PENDING" || r.status === "PENDING_REVIEW";
      return isPendingStatus && (
        !r.inference ||
        r.inference.intent === "MALICIOUS" ||
        r.inference.confidence < activeThreshold
      );
    }),
    [returns, activeThreshold]
  );

  const handleThresholdChange = (val: number) => {
    setActiveThreshold(val);
    if (onThresholdChange) onThresholdChange(val);
  };

  const processedHistory = React.useMemo(() =>
    (returns || []).filter((r: any) => ["PROCESSED", "DISMISSED", "FAILED", "ENQUEUED", "PROCESSING"].includes(r.status)),
    [returns]
  );

  const getOutcomeClass = (action: string) => {
    if (action.includes('Skip')) return 'outcome-skip';
    if (action.includes('Pause & Save')) return 'outcome-pause-save';
    if (action.includes('Pause & Replace')) return 'outcome-pause-replace';
    return 'outcome-dark-green';
  };

  const getIntentClass = (intent: string) => {
    switch (intent) {
      case 'QUALITY_DAMAGE': return 'intent-quality-damage';
      case 'FINANCIAL': return 'intent-financial';
      case 'SIZE_FIT': return 'intent-size-fit';
      case 'FLAVOR_PREFERENCE': return 'intent-flavor-preference';
      case 'OVERSTOCK': return 'intent-overstock';
      default: return 'intent-unknown';
    }
  };

  const filteredReturns = React.useMemo(() => {
    return (returns || []).filter((r: any) => {
      if (!r.inference) return false;
      const isPendingStatus = r.status === "PENDING" || r.status === "PENDING_REVIEW";
      const isMalicious = r.inference.intent === "MALICIOUS";
      return isPendingStatus && !isMalicious && r.inference.confidence >= activeThreshold;
    });
  }, [returns, activeThreshold]);

  return (
    <DemoModeProvider isDemoMode={isDemoMode}>
      <div style={{ paddingTop: '72px' }}>
      <style dangerouslySetInnerHTML={{
        __html: `
        .public-demo-banner {
          position: fixed;
          top: 0px;
          left: 0px;
          right: 0px;
          z-index: 9999;
          background: linear-gradient(90deg, rgb(0, 128, 96) 0%, rgb(0, 160, 119) 100%);
          color: white;
          padding: 12px 40px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 12px;
          gap: 24px;
          font-family: Inter, -apple-system, system-ui, sans-serif;
        }
        .install-free-btn {
          background-color: white;
          color: rgb(0, 128, 96);
          border: none;
          padding: 8px 20px;
          border-radius: 20px;
          font-weight: 700;
          cursor: pointer;
          font-size: 13px;
          box-shadow: rgba(0, 0, 0, 0.12) 0px 2px 6px;
          white-space: nowrap;
          transition: opacity 0.2s ease;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
        }
        .install-free-btn:hover {
          opacity: 0.9;
        }

        /* FAB */
        .fab-container {
          position: fixed;
          bottom: 28px;
          right: 28px;
          z-index: 1000;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 8px;
          font-family: Inter, -apple-system, system-ui, sans-serif;
        }
        .fab-bubble {
          background-color: white;
          border-radius: 12px;
          padding: 8px 14px;
          font-size: 12px;
          color: rgb(69, 79, 91);
          box-shadow: rgba(0, 0, 0, 0.12) 0px 2px 8px;
          font-weight: 500;
          position: relative;
        }
        .fab-bubble::after {
          content: '';
          position: absolute;
          bottom: -6px;
          right: 20px;
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 6px solid white;
        }
        .fab-button {
          background: linear-gradient(135deg, rgb(0, 128, 96) 0%, rgb(0, 160, 119) 100%);
          color: white;
          border: none;
          padding: 14px 28px;
          border-radius: 28px;
          font-weight: 700;
          cursor: pointer;
          box-shadow: rgba(0, 128, 96, 0.35) 0px 6px 20px;
          font-size: 15px;
          letter-spacing: 0.01em;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: transform 0.2s ease;
          text-decoration: none;
        }
        .fab-button:hover {
          transform: translateY(-2px);
        }

        /* Original Styles */
        .dashboard-bg-wrapper {
          background-color: #dff6e1;
          margin: 0px -28px 0 -28px;
          padding: 24px;
          min-height: calc(100vh - 48px);
        }
        .scrollable-table-container {
          height: 450px;
          overflow: auto !important;
          position: relative !important;
          background-color: var(--p-color-bg-surface);
          border: 1px solid var(--p-color-border-secondary);
          border-radius: var(--p-border-radius-200);
          box-shadow: var(--p-shadow-100);
        }
        .revivify-table-id-cell { width: 160px; min-width: 160px; font-weight: bold; }
        .revivify-table-action-cell { text-align: left !important; padding-left: var(--p-space-600) !important; font-variant-numeric: tabular-nums; }
        .revivify-table thead th.revivify-table-action-cell { text-align: left !important; }
        .revivify-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          font-size: var(--p-font-size-200);
        }
        .revivify-table thead th {
          position: sticky !important;
          top: 0 !important;
          z-index: 100;
          background-color: var(--p-color-bg-surface);
          padding: var(--p-space-200) var(--p-space-300);
          text-align: left;
          font-weight: var(--p-font-weight-medium);
          color: var(--p-color-text-subdued);
          border-bottom: 2px solid var(--p-color-border-secondary);
          white-space: nowrap;
        }
        .revivify-table tbody td {
          padding: var(--p-space-300);
          vertical-align: middle;
          border-bottom: 1px solid var(--p-color-border-secondary);
          background-color: var(--p-color-bg-surface);
        }
        .status-pulse {
          animation: statusPulse 1.5s ease-in-out infinite;
          display: inline-block;
        }
        @keyframes statusPulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); filter: brightness(1.2); }
          100% { transform: scale(1); }
        }
        .retry-ai-btn button {
          background: linear-gradient(180deg, #ff9b42 0%, #f47b20 100%) !important;
          border-color: #e66a00 !important;
          color: white !important;
        }
        .sticky-sidebar {
          position: sticky;
          top: 60px;
          align-self: flex-start;
        }
        @media (max-width: 767px) {
          .public-demo-banner {
            flex-direction: column;
            padding: 10px 16px;
            gap: 10px;
            align-items: flex-start;
          }
          .public-demo-banner > div:last-child {
            width: 100%;
            justify-content: space-between;
          }
          .sticky-sidebar {
            position: static;
          }
          .scrollable-table-container {
            height: 280px;
          }
          .fab-bubble {
            display: none;
          }
          .fab-button {
            padding: 12px 20px;
            font-size: 14px;
          }
        }
      ` }} />

      {/* Top Green Banner */}
      <div className="public-demo-banner">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>👀</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontWeight: 700, fontSize: '14px' }}>You're viewing a live demo</span>
            <span style={{ fontSize: '14px', opacity: 0.9 }}>— All data is simulated. Buttons are for preview only.</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ fontSize: '13px', opacity: 0.9, fontWeight: 500 }}>Ready to recover real revenue?</span>
          <a href="/" className="install-free-btn">
            Install Free →
          </a>
        </div>
      </div>

      <Page
        fullWidth
        title="Dashboard"
        primaryAction={{
          content: "Give Feedback",
          onAction: () => setFeedbackModalOpen(true),
          id: "header-feedback-btn",
        }}
        titleMetadata={null}
      >

        {showStatusBanner && bannerTicketId && (
          <Box paddingBlockEnd="400">
            <Banner
              title="Support Update Available"
              tone="success"
              onDismiss={handleDismissBanner}
            >
              <p>
                Your ticket <strong>#{bannerTicketId.split('-')[0]}</strong> status has been updated to <strong>{supportTickets.find(t => t.id === bannerTicketId)?.status}</strong>.
              </p>
            </Banner>
          </Box>
        )}

        <div className="dashboard-bg-wrapper">
          <div style={{ maxWidth: "1300px", margin: "0 auto", width: "100%" }}>
            <BlockStack gap="400">
              <AnalyticsOverview
                revenueData={revenueData}
                intentData={intentData}
                totalSavedRevenue={totalSavedRevenue}
                monthlyRevenueSaved={monthlyRevenueSaved}
                totalReturns={monthlyTotalInferences}
                automationRate={automationRate}
                annualizedSavings={annualizedSavings}
                retentionLift={retentionLift}
                roiMultiple={roiMultiple}
                avgSavedOrder={avgSavedOrder}
                shopLocale={shopLocale}
                shopCurrency={shopCurrency}
              />
              <Layout>
                <Layout.Section>
                  <BlockStack gap="500">
                    <Card>
                      <BlockStack gap="400">
                        <InlineStack align="space-between" blockAlign="center">
                          <Text as="h2" variant="headingMd">Human Review Queue ({pendingReview.length})</Text>
                          <Badge tone="attention">Critical</Badge>
                        </InlineStack>
                        <div className="scrollable-table-container">
                          <table className="revivify-table">
                            <thead>
                              <tr>
                                <th style={{ width: '160px' }}>ID</th>
                                <th>Note</th>
                                <th>AI Analysis</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pendingReview.map((item: any) => (
                                <tr key={item.id}>
                                  <td style={{ fontWeight: 'bold' }}>#{item.loopReturnId}</td>
                                  <td>
                                    <TruncatedNote note={item.customerNote} />
                                  </td>
                                  <td>
                                    {item.inference ? (
                                      <Badge tone={item.inference.intent === 'MALICIOUS' ? 'critical' : "attention"}>
                                        {`${formatActionText(item.inference.suggestedAction)} (${Math.round(item.inference.confidence * 100)}%)`}
                                      </Badge>
                                    ) : <Badge tone="attention">Analyzing…</Badge>}
                                  </td>
                                  <td style={{ textAlign: 'right' }}>
                                    <QueueItem item={item} hideDetails isReadOnly={isReadOnly} />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </BlockStack>
                    </Card>

                    <Card>
                      <BlockStack gap="400">
                        <InlineStack align="space-between" blockAlign="center">
                          <InlineStack gap="200" blockAlign="center">
                            <Text as="h2" variant="headingMd">⚠️ Needs Subscription ({noSubscriptionReturns.length})</Text>
                            <span className="action-required-badge">Action Required</span>
                          </InlineStack>
                        </InlineStack>
                        <div className="scrollable-table-container">
                          <table className="revivify-table">
                            <thead>
                              <tr>
                                <th style={{ width: '160px' }}>Return</th>
                                <th>Note</th>
                                <th>AI Intent</th>
                                <th style={{ textAlign: 'right' }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {noSubscriptionReturns.map((item: any) => (
                                <tr key={item.id}>
                                  <td style={{ fontWeight: 'bold' }}>#{item.loopReturnId}</td>
                                  <td><TruncatedNote note={item.customerNote} /></td>
                                  <td>
                                    <span className={`intent-badge ${getIntentClass(item.inference?.intent)}`}>
                                      {formatIntentText(item.inference?.intent)}
                                    </span>
                                  </td>
                                  <td style={{ textAlign: 'right' }}>
                                    <div className="full-width-button retry-ai-btn">
                                      <Button size="slim" onClick={() => {}} disabled={true}>Retry</Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </BlockStack>
                    </Card>

                    <Card>
                      <BlockStack gap="400">
                        <InlineStack align="space-between" blockAlign="center">
                          <Text as="h2" variant="headingMd">Live Retention Feed ({filteredReturns.length})</Text>
                          <Badge tone="success">Real-time</Badge>
                        </InlineStack>
                        <div className="scrollable-table-container">
                          <table className="revivify-table">
                            <thead>
                              <tr>
                                <th>Return</th>
                                <th>Note</th>
                                <th>AI Intent</th>
                                <th style={{ textAlign: 'right' }}>Status</th>
                                <th style={{ textAlign: 'right' }}>Revenue Saved</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredReturns.map((item: any) => (
                                <tr key={item.id}>
                                  <td style={{ fontWeight: 'bold' }}>#{item.loopReturnId}</td>
                                  <td><TruncatedNote note={item.customerNote} /></td>
                                  <td><Badge tone={getIntentTone(item.inference?.intent)}>{formatIntentText(item.inference?.intent)}</Badge></td>
                                  <td style={{ textAlign: 'right' }}>
                                    <Badge tone="success">
                                      {`${formatActionText(item.inference.suggestedAction)} (${Math.round(item.inference.confidence * 100)}%)`}
                                    </Badge>
                                  </td>
                                  <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                    ${Number(item.inference?.revenueSaved || 0).toFixed(2)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </BlockStack>
                    </Card>

                    <Card>
                      <BlockStack gap="400">
                        <InlineStack align="space-between" blockAlign="center">
                          <Text as="h2" variant="headingMd">Historical Saves ({processedHistory.length} - ${totalSavedRevenue.toLocaleString()})</Text>
                          <InlineStack gap="200">
                            <Button size="slim" onClick={onDownloadCSV} disabled={true}>Download CSV</Button>
                            <div className="dismiss-btn">
                              <Button size="slim" onClick={onDeleteHistory} disabled={true}>Delete History</Button>
                            </div>
                          </InlineStack>
                        </InlineStack>
                        <div className="scrollable-table-container">
                          <table className="revivify-table">
                            <thead>
                              <tr>
                                <th>Return</th>
                                <th>Customer</th>
                                <th>Outcome</th>
                                <th>Date</th>
                                <th style={{ textAlign: 'right' }}>Revenue</th>
                                <th style={{ textAlign: 'right' }}>Fee</th>
                              </tr>
                            </thead>
                            <tbody>
                              {processedHistory.map((item: any) => (
                                <tr key={item.id}>
                                  <td style={{ fontWeight: 'bold' }}>#{item.loopReturnId}</td>
                                  <td><Text variant="bodySm" tone="subdued">{item.customerEmail || "customer@example.com"}</Text></td>
                                  <td>
                                    <span className={`intent-badge ${getOutcomeClass(formatActionText(item.inference?.suggestedAction))}`}>
                                      {formatActionText(item.inference?.suggestedAction)}
                                    </span>
                                  </td>
                                  <td><Text variant="bodySm" tone="subdued">{new Date(item.createdAt || Date.now()).toLocaleDateString()}</Text></td>
                                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                    <span className="revenue-highlight">${Number(item.inference?.revenueSaved || 0).toFixed(2)}</span>
                                  </td>
                                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                    <Tooltip content={`${planName === 'enterprise' ? '0.5%' : planName === 'pro' ? '1%' : '3%'} Performance Fee on $${Number(item.inference?.revenueSaved || 0).toFixed(2)} saved revenue`}>
                                      <Text variant="bodySm" tone="subdued">${Number(item.inference?.feeCharged || 0).toFixed(2)}</Text>
                                    </Tooltip>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </BlockStack>
                    </Card>
                  </BlockStack>
                </Layout.Section>
                <Layout.Section variant="oneThird">
                  <div className="sticky-sidebar">
                    <BlockStack gap="500">
                    <UsageBalanceCard processed={monthlyProcessed} planName={planName} onUpgrade={() => {}} feesCharged={monthlyFeesCharged} isReadOnly={true} />
                    <AgentHealthCard settings={settings} planName={planName} isReadOnly={true} onThresholdChange={handleThresholdChange} threshold={settings?.confidenceThreshold} initialImpact={thresholdImpact} fetcher={fetcher} liveCount={filteredReturns.length} activeJobs={activeJobs} />
                    <IntegrationSettingsCard planName={planName} settings={settings} isReadOnly={true} rechargeConnected={true} loopConnected={true} />
                    <DangerZoneCard isDemoMode={isDemoMode} isReadOnly={true} recordCount={recordCount} onDownload={onDownloadAllCSV} isDownloading={false} sessionShop={sessionShop} shopHandle={shopHandle} planName={planName} appEnv={appEnv} />
                    <SupportCard isReadOnly={true} onContactSupport={onOpenSupport} tickets={supportTickets} onDeleteTicket={onDeleteTicket} />
                    </BlockStack>
                  </div>
                </Layout.Section>
              </Layout>
            </BlockStack>

            <Modal 
              open={feedbackModalOpen} 
              onClose={() => setFeedbackModalOpen(false)} 
              title="Share Feedback" 
              primaryAction={{ 
                content: "Submit Feedback", 
                onAction: () => {
                  if (onSubmitFeedback) onSubmitFeedback(feedbackText);
                  setFeedbackModalOpen(false);
                  setFeedbackText("");
                } 
              }}
            >
              <Modal.Section><FormLayout><TextField label="Feedback" value={feedbackText} onChange={setFeedbackText} multiline={4} autoComplete="off" /></FormLayout></Modal.Section>
            </Modal>
          </div>
        </div>
      </Page>

      {/* Floating Action Button */}
      <div className="fab-container">
        <div className="fab-bubble">Like what you see?</div>
        <a href="/" className="fab-button">
          <span>🚀</span> Install Revivify AI — Free
        </a>
      </div>
      </div>
    </DemoModeProvider>
  );
};
